import { Injector } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FilterModel, GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';

import { DatacenterType, UserDatacenterView } from '../../models/datacenter-aggrid-view';
import { OrderBy } from '../../utils/constant';
import { ColDefWithFields } from '../column-type';
import { CommonGridOptions } from '../grid-options';

import { StrategyAqdb } from './aqdb/strategy-aqdb';
import { StrategyOrder } from './orders/strategy-order';
import { StrategyBatches } from './batches/strategy-batch';
import { StrategyDocument } from './commercial-documents/strategy-document';
import { StrategyScheduleBusinessOrder } from './schedule-business-orders/strategy-schedule-business-order';
import { StrategyShipments } from './shipments/strategy-shipment';
import { StrategyQuality } from './quality-issues/strategy-quality';
import { StrategyOffer } from './offers/strategy-offer';
import { StrategyOrganization } from './organizations/strategy-organization';
import { StrategyUsers } from './users/strategy-users';
import { StrategyProtoType } from './prototypes/strategy-prototype';
import { Strategy } from './strategy';

export class ContextDatacenter {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private readonly strategy: Strategy<any>;

	constructor(datacenterType: DatacenterType, injector: Injector) {
		switch (datacenterType) {
		case 'order':
			this.strategy = new StrategyOrder(injector);
			break;
		case 'batch':
			this.strategy = new StrategyBatches(injector);
			break;
		case 'shipment':
			this.strategy = new StrategyShipments(injector);
			break;
		case 'offer':
			this.strategy = new StrategyOffer(injector);
			break;
		case 'quality':
			this.strategy = new StrategyQuality(injector);
			break;
		case 'document':
			this.strategy = new StrategyDocument(injector);
			break;
		case 'schedule-business-order':
			this.strategy = new StrategyScheduleBusinessOrder(injector);
			break;
		case 'organization':
			this.strategy = new StrategyOrganization(injector);
			break;
		case 'users':
			this.strategy = new StrategyUsers(injector);
			break;
		case 'prototypes':
			this.strategy = new StrategyProtoType(injector);
			break;
		case 'aqdb':
			this.strategy = new StrategyAqdb(injector);
			break;
		case 'calloff-by-batch':
			this.strategy = new StrategyBatches(injector);
			break;
		default:
			this.strategy = new StrategyOrder(injector);
			break;
		}
	}

	getDefaultOrderBy() {
		return this.strategy.defaultOrderBy;
	}

	hasTotal() {
		return this.strategy.hasTotal;
	}

	setTotal(api?: GridApi, filter?: string): Subscription {
		return this.strategy.setTotal(api, filter);
	}

	getAggregateFields(): string[] {
		return this.strategy.aggregateFields;
	}

	getDataFromGrid(
		orderBy: OrderBy<string>[],
		currentColumns: string[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<Record<string, unknown>>,
		rowsCountSubject: BehaviorSubject<number | undefined>,
		gridApi?: GridApi<Record<string, unknown>>
	) {
		return this.strategy.getDataFromGrid(orderBy, currentColumns, filter, params, rowsCountSubject, gridApi);
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<string>[] = [],
	) {
		return this.strategy.getAllDataSelected(currentColumns, filter, orderBy);
	}

	formatCellByType(columnDef: ColDefWithFields, data: unknown) {
		return this.strategy.formatCellByType(columnDef, data);
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return this.strategy.getCustomGridOptions(defaultGridOptions);
	}

	createAggregateCustomViewFilter(view: UserDatacenterView) {
		return this.strategy.createAggregateCustomViewFilter(view);
	}

	createAggregateQuickSearchFilter(
		currentSelectedView: UserDatacenterView,
		api?: GridApi,
		filterModel?: FilterModel
	) {
		return this.strategy.createAggregateQuickSearchFilter(currentSelectedView, api, filterModel);
	}
}
